import { useEffect, useState } from "react";
import { Filter, X } from "lucide-react";

/**
 * Filter popover/panel.
 *
 * Only three filters exist: Customer, Test Date, Report Date.
 * There is intentionally NO Location filter, dropdown, or state here —
 * location is only ever shown as a table column.
 *
 * Values are edited as a local draft and only pushed up to the parent
 * (and therefore only trigger a refetch) when "Apply Filters" is clicked.
 * "Clear Filters" resets both the draft and the applied filters.
 */
export default function ECGReportsFilters({
  isOpen,
  onClose,
  customers,
  appliedFilters,
  onApply,
  onClear,
  activeFilterCount,
}) {
  const [draft, setDraft] = useState(appliedFilters);

  // Re-sync the draft whenever the panel is opened, so it always reflects
  // whatever filters are currently applied (not stale edits from last time).
  useEffect(() => {
    if (isOpen) {
      setDraft(appliedFilters);
    }
  }, [isOpen, appliedFilters]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClear = () => {
    const cleared = { customer: "", test_date: "", report_date: "" };
    setDraft(cleared);
    onClear();
    onClose();
  };

  return (
    <>
      {/* Backdrop for mobile full-width panel / click-outside-to-close on desktop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent"
        onClick={onClose}
      />

      <div
        className="absolute right-0 z-50 mt-2 w-full max-w-sm rounded-xl border border-gray-200 bg-white p-4 shadow-lg
                   sm:right-0 sm:w-80
                   fixed inset-x-0 bottom-0 sm:static sm:inset-auto"
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Filter className="h-4 w-4" />
            Filters
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close filters"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Customer */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Customer
            </label>
            <select
              value={draft.customer}
              onChange={(e) => setDraft((d) => ({ ...d, customer: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900
                         focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            >
              <option value="">All Customers</option>
              {customers.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          {/* Test Date */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Test Date
            </label>
            <input
              type="date"
              value={draft.test_date}
              onChange={(e) => setDraft((d) => ({ ...d, test_date: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900
                         focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>

          {/* Report Date */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Report Date
            </label>
            <input
              type="date"
              value={draft.report_date}
              onChange={(e) => setDraft((d) => ({ ...d, report_date: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900
                         focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium
                       text-gray-700 hover:bg-gray-50"
          >
            Clear Filters
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}